'use client';

import { Box, Tab, Tabs } from '@mui/material';
import { type ReactNode, useId, useState } from 'react';
import { ElementViewed } from '@repo/segment/Analytics/ElementViewed';
import type { MarketplaceRateTableTabName } from '@repo/supergraph/structuredContent/hydration/MarketplaceRateTable/constants';

// TODO: we may change the name but are matching the original implementation
export const ELEMENT_IMPRESSION_NAME = 'dse_banking_table';

const ELEMENT_ADDITIONAL_ATTRIBUTES = { experience_type: 'rate_table' };

const SCROLLBAR_GAP_PX = 12;

export type RateTablePanel = {
  name: MarketplaceRateTableTabName;
  content: ReactNode;
};

type Props = {
  panels: RateTablePanel[];
  defaultIndex: number;
};

export function RateTableTabs({ panels, defaultIndex }: Props) {
  const [activeIndex, setActiveIndex] = useState(defaultIndex);
  const idPrefix = useId();

  return (
    <ElementViewed
      entityName={ELEMENT_IMPRESSION_NAME}
      sectionName={ELEMENT_IMPRESSION_NAME}
      itemPosition={0}
      additionalAttributes={ELEMENT_ADDITIONAL_ATTRIBUTES}
    >
      {({ fireElementInteracted }) => (
        // width/minWidth: without them this flex column (and any flex parent on the page) grows
        // to fit every tab, so the scrollable Tabs never overflow and can't be scrolled below md.
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            width: '100%',
            minWidth: 0,
          }}
        >
          <Tabs
            value={activeIndex}
            onChange={(_event, index: number) => {
              if (index === activeIndex) return;
              setActiveIndex(index);
              fireElementInteracted('click', {
                itemCopy: panels[index]?.name,
                itemPosition: index,
                additionalAttributes: ELEMENT_ADDITIONAL_ATTRIBUTES,
              });
            }}
            variant="scrollable"
            scrollButtons={false}
            visibleScrollbar
            aria-label="Rate table categories"
            sx={{
              minHeight: 0,
              minWidth: 0,
              maxWidth: '100%',
              '& .MuiTabs-indicator': { backgroundColor: 'primary.main' },
              '& .MuiTabs-scroller': {
                overflowX: 'auto !important',
                scrollbarWidth: 'thin',
                paddingBottom: `${SCROLLBAR_GAP_PX}px`,
              },
              // Once mounted, MUI renders the indicator in the scroller at `bottom: 0`, which is the
              // bottom of that padding (next to the scrollbar). Lift it by the same gap so it sits
              // on the divider. (Before mount MUI renders it inside the selected Tab instead.)
              '& .MuiTabs-scroller > .MuiTabs-indicator': { bottom: `${SCROLLBAR_GAP_PX}px` },
              '& .MuiTabs-list': {
                width: 'max-content',
                minWidth: '100%',
                borderBottom: '1px solid',
                borderColor: 'neutral.lighter',
              },
            }}
          >
            {panels.map((panel, index) => (
              <Tab
                key={panel.name}
                label={panel.name}
                id={`${idPrefix}-tab-${index}`}
                aria-controls={`${idPrefix}-panel-${index}`}
                // Matches the original rate table's tabs: a 44px-tall tab with the label centered
                // and 16px side padding, which puts the underline ~12px below the label.
                sx={{
                  minHeight: 44,
                  minWidth: 0,
                  paddingX: '16px',
                  paddingY: '8px',
                  fontSize: 12,
                  fontWeight: 500,
                  lineHeight: 1.1,
                  letterSpacing: '0.12em',
                  color: 'text.primary',
                  '&.Mui-selected': { color: 'primary.main', fontWeight: 700 },
                }}
              />
            ))}
          </Tabs>
          {panels.map((panel, index) => (
            <div
              key={panel.name}
              role="tabpanel"
              id={`${idPrefix}-panel-${index}`}
              aria-labelledby={`${idPrefix}-tab-${index}`}
              hidden={index !== activeIndex}
            >
              {panel.content}
            </div>
          ))}
        </Box>
      )}
    </ElementViewed>
  );
}
